'use client';

import { resetPassword } from '@/features/auth/reset-password-form/api/reset-password';
import { PASSWORD_RESET_PATHNAME } from '@/shared/constants/pathnames';
import { useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller } from 'react-hook-form';
import { ALLOWED_CHARS_REGEXP } from '@/shared/config/password';
import { Button } from '@/shared/ui/core';
import { PasswordInput } from '@/shared/ui/kit/password-input';
import { PasswordStrength, usePasswordStrength } from '@/shared/ui/kit/password-strength';
import { resetPasswordSchema, ResetPasswordFormData } from '@/shared/lib/validation/reset-password';
import styles from './reset-password-form.module.scss';


type ComponentProps = {
    _key?: string
}


export const ResetPasswordForm = ({ _key = '' }: ComponentProps) => {
    const router                         = useRouter();
    const [ isPending, startTransition ] = useTransition();
    const { control, handleSubmit, formState: { errors }, clearErrors } = useForm<ResetPasswordFormData>({
        resolver      : zodResolver(resetPasswordSchema),
        mode          : 'onBlur',
        reValidateMode: 'onChange',
        defaultValues : { password: '' },
    });

    const password = useWatch({ control, name: 'password' });
    const strength = usePasswordStrength(password ?? '');

    const onSubmit = handleSubmit((data) => {
        startTransition(async () => {
            const result = await resetPassword({ password: data.password, key: _key });
            if (result.ok) router.replace(PASSWORD_RESET_PATHNAME);
            // TODO: negative flow
        });
    });

    return (
        <div data-template="reset-password-form">
            <div className={ styles.controls }>
                <Controller
                    control={ control }
                    name="password"
                    render={ ({ field }) => (
                        <PasswordInput
                            label="New password:"
                            autoComplete="new-password"
                            value={ field.value }
                            onChange={ (e) => {
                                if (!ALLOWED_CHARS_REGEXP.test(e.target.value)) return;
                                field.onChange(e);
                            } }
                            onFocus={ () => clearErrors('password') }
                            onBlur={ () => field.onBlur() }
                            state={ errors.password ? 'error' : undefined }
                            error={ errors.password?.message }
                            PasswordStrength={
                                <PasswordStrength
                                    hint='And don&apos;t forget to follow a few rules to create a strong password:'
                                    level={ strength.level }
                                    rules={ strength.rules }
                                    touched={ !!password }
                                />
                            }
                        />
                    ) }
                />
            </div>

            <Button
                loading={ isPending }
                className={ styles.button }
                fullWidth
                onClick={ onSubmit }
            >
                Change my password
            </Button>
        </div>
    );
};
